import { useState, type FormEvent } from 'react'
import { useAuth, type SessionEnded } from '../AuthContext.js'
import { Button, PasswordInput } from '../../core/index.js'
import { BrandLogo } from '../../core/index.js'
import { Clock, LogIn, ShieldCheck } from 'lucide-react'
import { returnToOpener } from '../demoSession.js'

const INPUT_CLASS = 'w-full rounded border border-marine/25 bg-canvas px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine focus:bg-white focus:ring-2 focus:ring-marine/20'

/**
 * The client sign-in - operators, facility administrators and workers all
 * start here. Deliberately nothing like the admin sign-in (an ink-black
 * page with a coral bar): this one is the product's own ground, the deep
 * marine of the shell header with the header's logo above a pale card, so
 * nobody mistakes one screen for the other. The button is coral, as every
 * button is.
 */
export function Login({ title = 'Sign In' }: { title?: string }) {
  const { login, isVerifying, error, sessionEnded } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  // An admin whose demo session ran out has no client login to type here, so the form waits behind a link.
  const [showForm, setShowForm] = useState(false)

  if (sessionEnded?.openedByAdmin && !showForm) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-marine-dk px-4 py-10">
        <BrandLogo className="h-14 mb-6" />
        <AdminDemoEnded onShowForm={() => setShowForm(true)} />
      </div>
    )
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    void login(username, password)
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-marine-dk px-4 py-10">
      <BrandLogo className="h-14 mb-6" />
      {sessionEnded && <SessionEndedNotice ended={sessionEnded} />}
      <form onSubmit={handleSubmit} className="w-full max-w-sm rounded-lg bg-white border border-marine-lt p-8 shadow-xl">
        <h1 className="text-xl font-black text-marine-dk mb-1">{title}</h1>
        <p className="text-[11px] text-ink-mid mb-6">Operators, facility administrators and workers sign in here with their own login.</p>

        <label className="block text-xs font-bold text-marine-dk mb-1" htmlFor="username">
          Username
        </label>
        <input id="username" name="username" className={INPUT_CLASS} value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" autoFocus required />

        <label className="block text-xs font-bold text-marine-dk mb-1" htmlFor="password">
          Password
        </label>
        <PasswordInput id="password" name="password" className={INPUT_CLASS} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />

        {error && (
          <div role="alert" className="text-coral text-xs font-bold mb-4">
            {error}
          </div>
        )}

        <Button icon={LogIn} size="lg" fullWidth type="submit" disabled={isVerifying}>
          {isVerifying ? 'Signing in…' : 'Sign in'}
        </Button>

        <p className="text-[11px] text-ink-mid text-center mt-4">
          <a href="/forgot-password" className="font-bold text-marine hover:text-marine-dk">
            Forgot your password?
          </a>
        </p>
      </form>
    </div>
  )
}

/**
 * Why the reader is back at the sign-in page: their session stopped being accepted. A demo session an admin opened
 * never reaches here (`AdminDemoEnded` takes the page); one an operator opened is opened again with View as.
 */
function SessionEndedNotice({ ended }: { ended: SessionEnded }) {
  return (
    <div role="alert" className="w-full max-w-sm mb-4 border-2 border-coral bg-coral-lt p-4">
      <div className="flex items-start gap-2">
        <Clock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
        <div className="min-w-0">
          <h2 className="text-sm font-extrabold text-ink">{ended.demo ? 'This demo session has ended' : 'Your session has ended'}</h2>
          <p className="mt-0.5 text-[11px] text-ink leading-snug">
            {ended.demo
              ? 'Demo sessions last an hour. Open this person again with View as, after signing in below.'
              : 'For your security you were signed out. Sign in again to carry on where you were.'}
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The page a demo session a SeeWhoCares admin opened ends on. This tab never held the admin's sign-in and the person
 * it was opened as has no password anyone here knows, so the client sign-in form would only refuse them: the way on
 * is back to the admin area, closing this tab onto the one it was opened from where the browser allows.
 */
function AdminDemoEnded({ onShowForm }: { onShowForm: () => void }) {
  return (
    <div role="alert" className="w-full max-w-sm rounded-lg bg-white border border-marine-lt p-8 shadow-xl">
      <div className="flex items-center gap-2 mb-1">
        <Clock aria-hidden="true" className="h-5 w-5 shrink-0 text-coral" />
        <h1 className="text-xl font-black text-marine-dk">This demo session has ended</h1>
      </div>
      <p className="text-[11px] text-ink-mid leading-snug mb-6">
        You opened it from the admin area, and demo sessions last an hour. Go back to the admin area and open this person again with Open as.
      </p>
      <Button icon={ShieldCheck} size="lg" fullWidth onClick={() => returnToOpener(true)}>
        Back to the admin area
      </Button>
      <p className="text-[11px] text-ink-mid text-center mt-4">
        <button type="button" onClick={onShowForm} className="font-bold text-marine hover:text-marine-dk">
          Sign in with a client login instead
        </button>
      </p>
    </div>
  )
}
